import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import StatusBadge from "@/components/StatusBadge";
import { DEVICES } from "@/constants/testIds";
import { t } from "@/lib/i18n";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Trash2, Zap, Smartphone, Pencil } from "lucide-react";

const IPHONE_IMGS = [
  "https://images.unsplash.com/photo-1511707171634-5f897ff02aa9?crop=entropy&cs=srgb&fm=jpg&w=800&q=80",
  "https://images.unsplash.com/photo-1605636808063-ba999ff935eb?crop=entropy&cs=srgb&fm=jpg&w=800&q=80",
];

const NONE = "__none__"; // placeholder for "unlinked" in Selects (empty string is disallowed)

const emptyForm = {
  name: "",
  model: "iPhone 13",
  color: "Midnight",
  ios_version: "17.4",
  udid: "",
  status: "idle",
  battery: 100,
  image_url: IPHONE_IMGS[0],
  account_id: null,
  proxy_id: null,
};

const STATUSES = ["idle", "warming", "posting", "watching_stream", "commenting", "sleeping", "alert", "offline"];

export default function Devices() {
  const [devices, setDevices] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [personas, setPersonas] = useState([]);
  const [proxies, setProxies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null); // id or null
  const [form, setForm] = useState(emptyForm);

  const load = async () => {
    setLoading(true);
    try {
      const [d, a, p, pr] = await Promise.all([
        api.devices(),
        api.accounts(),
        api.personas(),
        api.proxies(),
      ]);
      setDevices(d);
      setAccounts(a);
      setPersonas(p);
      setProxies(pr);
    } catch { toast.error("Не удалось загрузить"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openNew = () => {
    setEditing(null);
    setForm({ ...emptyForm, image_url: IPHONE_IMGS[devices.length % IPHONE_IMGS.length] });
    setOpen(true);
  };

  const openEdit = (d) => {
    setEditing(d.id);
    setForm({
      name: d.name || "",
      model: d.model || "",
      color: d.color || "",
      ios_version: d.ios_version || "",
      udid: d.udid || "",
      status: d.status || "idle",
      battery: d.battery ?? 100,
      image_url: d.image_url || IPHONE_IMGS[0],
      account_id: d.account_id || null,
      proxy_id: d.proxy_id || null,
    });
    setOpen(true);
  };

  const submit = async () => {
    if (!form.name) return toast.error("Название обязательно");
    const payload = {
      ...form,
      battery: Number(form.battery) || 100,
      account_id: form.account_id || null,
      proxy_id: form.proxy_id || null,
    };
    try {
      if (editing) {
        await api.updateDevice(editing, payload);
        toast.success(t.common.saved);
      } else {
        await api.createDevice(payload);
        toast.success("Устройство добавлено");
      }
      setOpen(false);
      setForm(emptyForm);
      setEditing(null);
      load();
    } catch { toast.error(t.common.saveFailed); }
  };

  const del = async (id) => {
    if (!window.confirm(t.common.confirmDelete)) return;
    try {
      await api.deleteDevice(id);
      toast.success(t.common.deleted);
      load();
    } catch { toast.error(t.common.failed); }
  };

  const ping = async (id) => {
    try {
      await api.pingDevice(id);
      toast.success(t.devices.pinged);
      load();
    } catch { toast.error(t.common.failed); }
  };

  const accountFor = (id) => accounts.find((a) => a.id === id);
  const personaFor = (id) => personas.find((p) => p.id === id);
  const proxyFor = (id) => proxies.find((p) => p.id === id);

  return (
    <div data-testid={DEVICES.root}>
      <PageHeader
        title={t.devices.title}
        description={t.devices.description}
        actions={
          <button
            data-testid={DEVICES.addBtn}
            onClick={openNew}
            className="inline-flex items-center gap-2 px-3.5 py-2 rounded-xl bg-emerald-500 hover:bg-emerald-600 text-white text-sm font-medium transition-colors"
          >
            <Plus className="h-4 w-4" /> {t.devices.addDevice}
          </button>
        }
      />

      {loading ? (
        <div className="text-slate-400 text-sm">{t.common.loading}</div>
      ) : devices.length === 0 ? (
        <div className="card-soft p-10 text-center">
          <Smartphone className="h-10 w-10 text-slate-300 mx-auto" />
          <div className="font-display text-lg mt-3 text-slate-900">{t.devices.empty}</div>
          <div className="text-sm text-slate-500 mt-1">{t.devices.emptyHint}</div>
        </div>
      ) : (
        <div className="space-y-3">
          {devices.map((d) => {
            const acc = accountFor(d.account_id);
            const persona = acc ? personaFor(acc.persona_id) : null;
            const proxy = proxyFor(d.proxy_id);
            return (
              <div key={d.id} className="card-soft p-5 flex flex-col md:flex-row md:items-center gap-5">
                <div className="w-24 h-24 flex-shrink-0 rounded-xl bg-gradient-to-b from-slate-50 to-white flex items-center justify-center overflow-hidden">
                  <img src={d.image_url || IPHONE_IMGS[0]} alt="" className="h-full object-contain" />
                </div>
                <div className="flex-1 min-w-0 grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div>
                    <div className="text-xs uppercase tracking-widest text-slate-400">{t.devices.fields.device}</div>
                    <div className="font-display font-semibold text-slate-900 mt-1">{d.name}</div>
                    <div className="text-xs text-slate-500">{d.model} · {d.color} · iOS {d.ios_version}</div>
                    <div className="mt-2"><StatusBadge status={d.status} /></div>
                  </div>
                  <div>
                    <div className="text-xs uppercase tracking-widest text-slate-400">{t.devices.fields.account}</div>
                    {acc ? (
                      <>
                        <div className="font-medium text-slate-900 mt-1">{acc.username}</div>
                        <div className="text-xs text-slate-500">{acc.followers} подп · {t.accounts.stage[acc.stage]}</div>
                      </>
                    ) : (
                      <div className="text-sm text-slate-400 mt-1">{t.common.unlinked}</div>
                    )}
                  </div>
                  <div>
                    <div className="text-xs uppercase tracking-widest text-slate-400">{t.devices.fields.persona}</div>
                    {persona ? (
                      <div className="flex items-center gap-2 mt-1">
                        <img src={persona.avatar_url} className="h-7 w-7 rounded-full object-cover" alt="" />
                        <div>
                          <div className="text-sm font-medium text-slate-900">{persona.name}</div>
                          <div className="text-[11px] text-slate-500">{persona.geo} · {persona.language}</div>
                        </div>
                      </div>
                    ) : (
                      <div className="text-sm text-slate-400 mt-1">{t.common.none}</div>
                    )}
                  </div>
                  <div>
                    <div className="text-xs uppercase tracking-widest text-slate-400">{t.devices.fields.proxy}</div>
                    {proxy ? (
                      <>
                        <div className="text-sm font-medium text-slate-900 mt-1">{proxy.provider}</div>
                        <div className="text-[11px] text-slate-500">{t.proxies.type[proxy.type]} · {proxy.geo}</div>
                      </>
                    ) : (
                      <div className="text-sm text-slate-400 mt-1">{t.common.none}</div>
                    )}
                  </div>
                </div>
                <div className="flex gap-2 shrink-0">
                  <button
                    data-testid={DEVICES.pingBtn(d.id)}
                    onClick={() => ping(d.id)}
                    className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-slate-100 hover:bg-emerald-100 hover:text-emerald-700 text-slate-700 text-xs font-medium transition-colors"
                  >
                    <Zap className="h-3.5 w-3.5" /> {t.devices.ping}
                  </button>
                  <button
                    data-testid={DEVICES.editBtn(d.id)}
                    onClick={() => openEdit(d)}
                    className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-medium transition-colors"
                  >
                    <Pencil className="h-3.5 w-3.5" />
                  </button>
                  <button
                    data-testid={DEVICES.deleteBtn(d.id)}
                    onClick={() => del(d.id)}
                    className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-slate-100 hover:bg-rose-100 hover:text-rose-700 text-slate-700 text-xs font-medium transition-colors"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-2xl max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-display">
              {editing ? t.devices.editDevice : t.devices.newDevice}
            </DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <Label className="text-xs uppercase tracking-widest text-slate-500">{t.devices.fields.name}</Label>
              <Input
                data-testid={DEVICES.form.name}
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Телефон C"
                className="mt-1"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-slate-500">{t.devices.fields.model}</Label>
              <Input data-testid={DEVICES.form.model} value={form.model} onChange={(e) => setForm({ ...form, model: e.target.value })} className="mt-1" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-slate-500">{t.devices.fields.color}</Label>
              <Input data-testid={DEVICES.form.color} value={form.color} onChange={(e) => setForm({ ...form, color: e.target.value })} className="mt-1" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-slate-500">{t.devices.fields.ios}</Label>
              <Input data-testid={DEVICES.form.ios} value={form.ios_version} onChange={(e) => setForm({ ...form, ios_version: e.target.value })} className="mt-1" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-slate-500">{t.devices.fields.udid} ({t.common.optional})</Label>
              <Input data-testid={DEVICES.form.udid} value={form.udid} onChange={(e) => setForm({ ...form, udid: e.target.value })} className="mt-1 font-mono text-xs" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-slate-500">{t.devices.fields.status}</Label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {STATUSES.map((s) => <SelectItem key={s} value={s}>{t.devices.status[s]}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-slate-500">{t.devices.fields.battery}</Label>
              <Input type="number" value={form.battery} onChange={(e) => setForm({ ...form, battery: e.target.value })} className="mt-1" />
            </div>
            <div className="col-span-2">
              <Label className="text-xs uppercase tracking-widest text-slate-500">{t.devices.fields.imageUrl}</Label>
              <Input value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })} className="mt-1 font-mono text-xs" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-slate-500">{t.devices.fields.account}</Label>
              <Select
                value={form.account_id || NONE}
                onValueChange={(v) => setForm({ ...form, account_id: v === NONE ? null : v })}
              >
                <SelectTrigger className="mt-1"><SelectValue placeholder={t.common.unlinked} /></SelectTrigger>
                <SelectContent>
                  <SelectItem value={NONE}>{t.common.unlinked}</SelectItem>
                  {accounts.map((a) => <SelectItem key={a.id} value={a.id}>{a.username}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-slate-500">{t.devices.fields.proxy}</Label>
              <Select
                value={form.proxy_id || NONE}
                onValueChange={(v) => setForm({ ...form, proxy_id: v === NONE ? null : v })}
              >
                <SelectTrigger className="mt-1"><SelectValue placeholder={t.common.unlinked} /></SelectTrigger>
                <SelectContent>
                  <SelectItem value={NONE}>{t.common.unlinked}</SelectItem>
                  {proxies.map((p) => <SelectItem key={p.id} value={p.id}>{p.provider} · {p.geo}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <button
              data-testid={DEVICES.saveBtn}
              onClick={submit}
              className="px-4 py-2 rounded-xl bg-emerald-500 hover:bg-emerald-600 text-white text-sm font-medium transition-colors"
            >
              {editing ? t.common.saveChanges : t.common.save}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
